import { useEffect, useRef, useCallback, useState, Component, ReactNode } from 'react';

// ── Error boundary ──────────────────────────────────────────────────────────
class EditorBoundary extends Component<{ children: ReactNode }, { err: boolean }> {
  constructor(p: { children: ReactNode }) { super(p); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    if (this.state.err) return (
      <div className="border border-red-200 rounded-lg p-4 bg-red-50 text-sm text-red-500">
        <i className="ri-error-warning-line mr-1" />Editor error — please refresh the page.
      </div>
    );
    return this.props.children;
  }
}

// ── Types ───────────────────────────────────────────────────────────────────
interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

// ── Toolbar button ──────────────────────────────────────────────────────────
function ToolBtn({
  icon, title, active, onAction,
}: { icon: string; title: string; active?: boolean; onAction: () => void }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); onAction(); }}
      className={`w-7 h-7 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer select-none ${
        active ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
      }`}
    >
      <i className={icon} />
    </button>
  );
}

// ── Main editor ─────────────────────────────────────────────────────────────
function Editor({ value, onChange, placeholder = 'Start typing…', minHeight = 320 }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const initDone = useRef(false);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());

  // Load initial HTML once
  useEffect(() => {
    if (initDone.current || !editorRef.current) return;
    initDone.current = true;
    if (value && value.trim()) {
      editorRef.current.innerHTML = value.trim().startsWith('<')
        ? value
        : value.split('\n').map(l => `<p>${l || '<br>'}</p>`).join('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const updateActiveFormats = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const formats = new Set<string>();
    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('underline')) formats.add('underline');
    setActiveFormats(formats);
  }, []);

  const exec = useCallback((cmd: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    emitChange();
    updateActiveFormats();
  }, [emitChange, updateActiveFormats]);

  const insertHeading = useCallback((tag: 'h1' | 'h2' | 'h3') => {
    editorRef.current?.focus();
    // Check if already a heading of that type — toggle back to p
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const node = sel.getRangeAt(0).commonAncestorContainer;
      const el = node.nodeType === 3 ? node.parentElement : node as Element;
      const block = el?.closest('h1,h2,h3,p,div');
      if (block && block.tagName.toLowerCase() === tag) {
        document.execCommand('formatBlock', false, 'p');
      } else {
        document.execCommand('formatBlock', false, tag);
      }
    } else {
      document.execCommand('formatBlock', false, tag);
    }
    emitChange();
  }, [emitChange]);

  const getBlockType = useCallback((): string => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 'p';
    const node = sel.getRangeAt(0).commonAncestorContainer;
    const el = node.nodeType === 3 ? node.parentElement : node as Element;
    const block = el?.closest('h1,h2,h3,ul,ol,p,div,blockquote');
    return block ? block.tagName.toLowerCase() : 'p';
  }, []);

  const [blockType, setBlockType] = useState('p');

  const handleSelChange = useCallback(() => {
    updateActiveFormats();
    setBlockType(getBlockType());
  }, [updateActiveFormats, getBlockType]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelChange);
    return () => document.removeEventListener('selectionchange', handleSelChange);
  }, [handleSelChange]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-400 focus-within:border-brand-400 transition-all">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
        <ToolBtn icon="ri-arrow-go-back-line" title="Undo" onAction={() => exec('undo')} />
        <ToolBtn icon="ri-arrow-go-forward-line" title="Redo" onAction={() => exec('redo')} />
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        <ToolBtn icon="ri-h-1" title="Heading 1" active={blockType === 'h1'} onAction={() => insertHeading('h1')} />
        <ToolBtn icon="ri-h-2" title="Heading 2" active={blockType === 'h2'} onAction={() => insertHeading('h2')} />
        <ToolBtn icon="ri-h-3" title="Heading 3" active={blockType === 'h3'} onAction={() => insertHeading('h3')} />
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        <ToolBtn icon="ri-bold" title="Bold" active={activeFormats.has('bold')} onAction={() => exec('bold')} />
        <ToolBtn icon="ri-italic" title="Italic" active={activeFormats.has('italic')} onAction={() => exec('italic')} />
        <ToolBtn icon="ri-underline" title="Underline" active={activeFormats.has('underline')} onAction={() => exec('underline')} />
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        <ToolBtn icon="ri-list-unordered" title="Bullet List" active={blockType === 'ul'} onAction={() => exec('insertUnorderedList')} />
        <ToolBtn icon="ri-list-ordered" title="Numbered List" active={blockType === 'ol'} onAction={() => exec('insertOrderedList')} />
        <div className="w-px h-5 bg-gray-200 mx-0.5" />
        <ToolBtn icon="ri-format-clear" title="Clear Formatting" onAction={() => exec('removeFormat')} />
      </div>

      {/* Editable area */}
      <div className="relative bg-white">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onKeyUp={handleSelChange}
          onMouseUp={handleSelChange}
          className="px-4 py-3 text-sm text-gray-800 leading-relaxed focus:outline-none rte-editor-area"
          style={{ minHeight }}
          data-placeholder={placeholder}
        />
      </div>

      <style>{`
        .rte-editor-area:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rte-editor-area h1 { font-size:1.5rem; font-weight:800; margin:0.75rem 0 0.25rem; color:#111827; line-height:1.3; }
        .rte-editor-area h2 { font-size:1.2rem; font-weight:700; margin:0.6rem 0 0.2rem; color:#1f2937; }
        .rte-editor-area h3 { font-size:1rem; font-weight:700; margin:0.5rem 0 0.15rem; color:#374151; }
        .rte-editor-area ul { list-style-type:disc; padding-left:1.5rem; margin:0.4rem 0; }
        .rte-editor-area ol { list-style-type:decimal; padding-left:1.5rem; margin:0.4rem 0; }
        .rte-editor-area li { margin:0.2rem 0; }
        .rte-editor-area blockquote { border-left:3px solid #e5e7eb; padding-left:1rem; color:#6b7280; font-style:italic; margin:0.5rem 0; }
        .rte-editor-area p { margin:0.2rem 0; }
        .rte-editor-area b, .rte-editor-area strong { font-weight:700; }
        .rte-editor-area i, .rte-editor-area em { font-style:italic; }
        .rte-editor-area u { text-decoration:underline; }
      `}</style>
    </div>
  );
}

// ── Export wrapped in error boundary ────────────────────────────────────────
export default function RichTextEditor(props: RichTextEditorProps) {
  return (
    <EditorBoundary>
      <Editor {...props} />
    </EditorBoundary>
  );
}
